"use client";

import { useMemo, useState } from "react";

import { CancelSmallIcon, Layers4675259Icon } from "@/components/icons-lib";
import { MapPanel } from "@/components/maps/map-panel";
import { cn } from "@/shared/helpers/cn.helper";

import { FindMySchoolLayersContent } from "./find-my-school-layers-content";
import { useFindMySchoolLayers } from "./find-my-school-layers-context";

export interface FindMySchoolLayersPanelProps {
  hasDistrictLayer?: boolean;
  className?: string;
  mobile?: boolean;
  controlsBottom?: number;
  controlsOpacity?: number;
}

/** Find My School map menu: same panel UI as panel map (Layers only, no title / National / New Housing). */
export function FindMySchoolLayersPanel({
  hasDistrictLayer = false,
  className,
  mobile = false,
  controlsBottom = 16,
  controlsOpacity = 1,
}: FindMySchoolLayersPanelProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const {
    districtVisible,
    schoolBoundariesHiddenTypes,
    districtSchoolLevelTypes,
    publishedMapLevels,
    mapLevelsHiddenIds,
  } = useFindMySchoolLayers();

  const activeLayersCount = useMemo(() => {
    const districtCount = hasDistrictLayer && districtVisible ? 1 : 0;
    const boundariesVisibleCount = districtSchoolLevelTypes.filter(
      (t) => !schoolBoundariesHiddenTypes.includes(t),
    ).length;
    const mapLevelsVisibleCount = publishedMapLevels.filter(
      (level) => !mapLevelsHiddenIds.includes(level.id),
    ).length;
    return districtCount + boundariesVisibleCount + mapLevelsVisibleCount;
  }, [
    hasDistrictLayer,
    districtVisible,
    districtSchoolLevelTypes,
    schoolBoundariesHiddenTypes,
    publishedMapLevels,
    mapLevelsHiddenIds,
  ]);

  const indicator = useMemo(() => {
    if (activeLayersCount <= 0) return null;
    return (
      <span
        className={cn(
          "flex h-[17px] w-[17px] min-w-[17px] items-center justify-center rounded-full",
          "border border-white bg-[#A11733]",
          "text-[10px]/[12px] font-medium text-white",
        )}
      >
        {activeLayersCount}
      </span>
    );
  }, [activeLayersCount]);

  const tabs = useMemo(
    () => [
      {
        key: "layers",
        label: "Layers",
        icon: <Layers4675259Icon className="h-5 w-5" />,
        indicator,
        content: <FindMySchoolLayersContent hasDistrictLayer={hasDistrictLayer} />,
      },
    ],
    [hasDistrictLayer, indicator],
  );

  if (mobile) {
    return (
      <div className={cn("pointer-events-none absolute inset-0 z-[35]", className)}>
        {!mobileOpen && (
          <button
            aria-label="Layers"
            className="pointer-events-auto absolute right-[14px] flex h-[52px] w-[52px] items-center justify-center rounded-full border border-[#EDECEB] bg-white text-[#3F3A3B] shadow-[0_2px_8px_rgba(0,0,0,0.18)]"
            style={{
              bottom: controlsBottom,
              opacity: controlsOpacity,
              pointerEvents: controlsOpacity > 0.05 ? "auto" : "none",
              transition: "bottom 220ms cubic-bezier(.2,.8,.2,1), opacity 160ms linear",
            }}
            type="button"
            onClick={() => setMobileOpen(true)}
          >
            <Layers4675259Icon className="h-6 w-6" />
            {indicator && <span className="absolute -right-1 -top-1">{indicator}</span>}
          </button>
        )}
        {mobileOpen && (
          <section
            aria-label="Map layers"
            className="pointer-events-auto absolute left-4 right-4 top-[76px] overflow-y-auto rounded-[12px] border border-[#EDECEB] bg-white p-3 shadow-lg"
            style={{ bottom: controlsBottom + 66 }}
            onKeyDown={(event) => {
              if (event.key === "Escape") setMobileOpen(false);
            }}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-[16px]/[22px] font-semibold text-[#161515]">Layers</h2>
              <button
                aria-label="Close layers"
                className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-black/5"
                type="button"
                onClick={() => setMobileOpen(false)}
              >
                <CancelSmallIcon className="h-5 w-5" />
              </button>
            </div>
            <FindMySchoolLayersContent hasDistrictLayer={hasDistrictLayer} />
          </section>
        )}
      </div>
    );
  }

  return (
    <MapPanel
      className={className}
      defaultTabKey="layers"
      side="left"
      tabs={tabs}
    >
      <MapPanel.Tabs />
      <MapPanel.SideMenu
        contentHeight="fit"
        widthClassName="w-fit"
      />
    </MapPanel>
  );
}
